import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { appointmentsAPI, barbersAPI, clientsAPI, servicesAPI } from '../lib/api';
import SearchableSelect from '../components/SearchableSelect';
import { 
  formatTime, 
  formatDate, 
  getTodayDate, 
  generateTimeSlots, 
  calculateEndTime,
  appointmentStatusLabels,
  cn
} from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  Clock, 
  User,
  Scissors,
  X,
  Check,
  Play,
  XCircle,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const TIME_SLOTS = generateTimeSlots('08:00', '21:00', 30);

export default function AgendaPage() {
  const { selectedBusiness } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [formData, setFormData] = useState({
    client_id: '',
    barber_id: '',
    service_ids: [],
    date: getTodayDate(),
    start_time: '09:00',
    notes: ''
  });

  const dateStr = selectedDate.toISOString().split('T')[0];

  const loadData = useCallback(async () => {
    if (!selectedBusiness) return;
    
    try {
      setLoading(true);
      const [aptsRes, barbersRes, clientsRes, servicesRes] = await Promise.all([
        appointmentsAPI.getAll({ date: dateStr }),
        barbersAPI.getAll({ exclude_admin_collab: true }),
        clientsAPI.getAll({ limit: 5000 }),
        servicesAPI.getAll()
      ]);
      
      setAppointments(aptsRes.data);
      setBarbers(barbersRes.data);
      setClients(clientsRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      console.error('Error loading agenda:', error);
      toast.error('Error al cargar la agenda');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness, dateStr]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePrevDay = () => {
    const prev = new Date(selectedDate);
    prev.setDate(prev.getDate() - 1);
    setSelectedDate(prev);
  };

  const handleNextDay = () => {
    const next = new Date(selectedDate);
    next.setDate(next.getDate() + 1);
    setSelectedDate(next);
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  const openNewAppointment = (time, barberId) => {
    setSelectedAppointment(null);
    setFormData({
      client_id: '',
      barber_id: barberId || '',
      service_ids: [],
      date: dateStr,
      start_time: time || '09:00',
      notes: ''
    });
    setDialogOpen(true);
  };

  const openEditAppointment = (apt) => {
    setSelectedAppointment(apt);
    setFormData({
      client_id: apt.client_id,
      barber_id: apt.barber_id,
      service_ids: apt.service_ids,
      date: apt.date,
      start_time: apt.start_time,
      notes: apt.notes || ''
    });
    setDialogOpen(true);
  };

  const calculateTotalDuration = () => {
    return formData.service_ids.reduce((total, serviceId) => {
      const service = services.find(s => s.id === serviceId);
      return total + (service?.duration_minutes || 30);
    }, 0);
  };

  const handleSubmit = async () => {
    if (!formData.client_id || !formData.barber_id || formData.service_ids.length === 0) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    const duration = calculateTotalDuration();
    const end_time = calculateEndTime(formData.start_time, duration);

    try {
      if (selectedAppointment) {
        await appointmentsAPI.update(selectedAppointment.id, {
          ...formData,
          end_time
        });
        toast.success('Cita actualizada');
      } else {
        await appointmentsAPI.create({
          ...formData,
          business_id: selectedBusiness.id,
          end_time
        });
        toast.success('Cita creada');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving appointment:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar la cita');
    }
  };

  const handleStatusChange = async (apt, newStatus) => {
    try {
      await appointmentsAPI.update(apt.id, { status: newStatus });
      toast.success('Estado actualizado');
      loadData();
    } catch (error) {
      toast.error('Error al actualizar el estado');
    }
  };

  const handleCancel = async (apt) => {
    try {
      await appointmentsAPI.delete(apt.id);
      toast.success('Cita cancelada');
      loadData();
    } catch (error) {
      toast.error('Error al cancelar la cita');
    }
  };

  const getAppointmentForSlot = (time, barberId) => {
    return appointments.find(apt => 
      apt.barber_id === barberId &&
      apt.start_time <= time &&
      apt.end_time > time &&
      apt.status !== 'cancelled'
    );
  };

  const getClient = (clientId) => clients.find(c => c.id === clientId);
  const getBarber = (barberId) => barbers.find(b => b.id === barberId);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="agenda-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Agenda
          </h1>
          <p className="text-slate-500 mt-1">
            Gestiona las citas de {selectedBusiness?.name}
          </p>
        </div>
        <Button 
          onClick={() => openNewAppointment()}
          className="bg-primary text-white hover:bg-primary/90"
          data-testid="new-appointment-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva cita
        </Button>
      </div>

      {/* Date Navigation */}
      <Card className="border-slate-200 bg-white">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={handlePrevDay} className="border-slate-200">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={handleNextDay} className="border-slate-200">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={handleToday} className="border-slate-200 ml-2">
                Hoy
              </Button>
            </div>
            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope' }}>
              {formatDate(selectedDate, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </h2>
            <div className="text-sm text-slate-500">
              {appointments.filter(a => a.status !== 'cancelled').length} citas
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calendar Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Mini Calendar */}
        <Card className="border-slate-200 bg-white">
          <CardHeader>
            <CardTitle className="text-sm font-medium text-slate-600">Calendario</CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              className="rounded-md"
            />
          </CardContent>
        </Card>

        {/* Schedule Grid */}
        <Card className="lg:col-span-3 border-slate-200 bg-white overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-96">
                <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
              </div>
            ) : barbers.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-96 text-zinc-500">
                <User className="w-12 h-12 mb-3 opacity-50" />
                <p>No hay barberos/estilistas registrados</p>
                <Button variant="link" className="mt-2">
                  Agregar barbero
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="p-3 text-left text-xs font-medium text-slate-500 w-20">Hora</th>
                      {barbers.map(barber => (
                        <th key={barber.id} className="p-3 text-left text-xs font-medium text-slate-500 min-w-[200px]">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                              <User className="w-4 h-4 text-slate-600" />
                            </div>
                            <span className="text-slate-700">{barber.display_name}</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {TIME_SLOTS.map((time) => (
                      <tr key={time} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-2 text-xs text-slate-500 font-mono">{formatTime(time)}</td>
                        {barbers.map(barber => {
                          const apt = getAppointmentForSlot(time, barber.id);
                          const isStart = apt && apt.start_time === time;
                          
                          if (apt && !isStart) {
                            return <td key={`${time}-${barber.id}`} className="p-1"></td>;
                          }
                          
                          return (
                            <td key={`${time}-${barber.id}`} className="p-1">
                              {apt ? (
                                <div 
                                  className={cn(
                                    "p-2 rounded-md cursor-pointer transition-all hover:ring-2 hover:ring-primary/20",
                                    `status-${apt.status}`
                                  )}
                                  onClick={() => openEditAppointment(apt)}
                                  data-testid={`appointment-${apt.id}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium">
                                      {formatTime(apt.start_time)} - {formatTime(apt.end_time)}
                                    </span>
                                    <Badge variant="outline" className="text-[10px] px-1">
                                      {appointmentStatusLabels[apt.status]}
                                    </Badge>
                                  </div>
                                  <p className="text-sm font-medium mt-1 truncate">
                                    {getClient(apt.client_id)?.first_name || 'Cliente'} {getClient(apt.client_id)?.last_name?.charAt(0)}.
                                  </p>
                                  <p className="text-xs text-slate-500 mt-0.5 truncate">
                                    {apt.service_ids.length} servicio(s)
                                  </p>
                                </div>
                              ) : (
                                <div 
                                  className="h-12 rounded-md border border-dashed border-slate-300 hover:border-primary hover:bg-primary/5 cursor-pointer transition-all flex items-center justify-center group"
                                  onClick={() => openNewAppointment(time, barber.id)}
                                >
                                  <Plus className="w-4 h-4 text-slate-400 group-hover:text-primary" />
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Appointment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {selectedAppointment ? 'Editar cita' : 'Nueva cita'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Client */}
            <div className="space-y-2">
              <Label>Cliente *</Label>
              <SearchableSelect
                items={clients}
                value={formData.client_id || ''}
                onChange={(c) => setFormData({ ...formData, client_id: c ? c.id : '' })}
                getLabel={(c) => `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || 'Sin nombre'}
                getSubLabel={(c) => c.phone || c.email || ''}
                searchFields={(c) => [c.first_name, c.last_name, c.phone, c.email].filter(Boolean)}
                placeholder="Buscar cliente por nombre, teléfono o email…"
                emptyLabel="Sin coincidencias"
                testId="select-client"
              />
            </div>

            {/* Barber */}
            <div className="space-y-2">
              <Label>Barbero/Estilista *</Label>
              <Select 
                value={formData.barber_id} 
                onValueChange={(value) => setFormData({ ...formData, barber_id: value })}
              >
                <SelectTrigger className="bg-white border-slate-200" data-testid="select-barber">
                  <SelectValue placeholder="Seleccionar barbero" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {barbers.map(barber => (
                    <SelectItem key={barber.id} value={barber.id}>
                      {barber.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Services */}
            <div className="space-y-2">
              <Label>Servicios *</Label>
              <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 bg-slate-50 rounded-md">
                {services.map(service => (
                  <label 
                    key={service.id} 
                    className={cn(
                      "flex items-center gap-2 p-2 rounded cursor-pointer transition-colors",
                      formData.service_ids.includes(service.id) 
                        ? "bg-primary text-white" 
                        : "hover:bg-slate-100"
                    )}
                  >
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={formData.service_ids.includes(service.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({ ...formData, service_ids: [...formData.service_ids, service.id] });
                        } else {
                          setFormData({ ...formData, service_ids: formData.service_ids.filter(id => id !== service.id) });
                        }
                      }}
                    />
                    <Scissors className="w-3 h-3" />
                    <span className="text-xs truncate">{service.name}</span>
                  </label>
                ))}
              </div>
              {formData.service_ids.length > 0 && (
                <p className="text-xs text-slate-500">
                  Duración total: {calculateTotalDuration()} min
                </p>
              )}
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="bg-white border-slate-200"
                  data-testid="input-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Hora *</Label>
                <Select 
                  value={formData.start_time} 
                  onValueChange={(value) => setFormData({ ...formData, start_time: value })}
                >
                  <SelectTrigger className="bg-white border-slate-200" data-testid="select-time">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <ScrollArea className="h-48">
                      {TIME_SLOTS.map(time => (
                        <SelectItem key={time} value={time}>
                          {formatTime(time)}
                        </SelectItem>
                      ))}
                    </ScrollArea>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notas adicionales..."
                className="bg-white border-slate-200 resize-none"
                rows={2}
              />
            </div>

            {/* Status Actions */}
            {selectedAppointment && (
              <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
                {selectedAppointment.status === 'pending' && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                    onClick={() => handleStatusChange(selectedAppointment, 'confirmed')}
                  >
                    <Check className="w-3 h-3 mr-1" /> Confirmar
                  </Button>
                )}
                {['pending', 'confirmed'].includes(selectedAppointment.status) && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                    onClick={() => handleStatusChange(selectedAppointment, 'in_progress')}
                  >
                    <Play className="w-3 h-3 mr-1" /> Iniciar
                  </Button>
                )}
                {selectedAppointment.status === 'in_progress' && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
                    onClick={() => handleStatusChange(selectedAppointment, 'completed')}
                  >
                    <Check className="w-3 h-3 mr-1" /> Completar
                  </Button>
                )}
                {selectedAppointment.status !== 'cancelled' && selectedAppointment.status !== 'completed' && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                    onClick={() => handleCancel(selectedAppointment)}
                  >
                    <XCircle className="w-3 h-3 mr-1" /> Cancelar
                  </Button>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-200">
              Cerrar
            </Button>
            <Button onClick={handleSubmit} className="bg-primary text-white hover:bg-primary/90" data-testid="save-appointment-btn">
              {selectedAppointment ? 'Actualizar' : 'Crear cita'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
